import moment from 'moment';
import { useEffect, useRef, useState } from 'react';
import { callApi } from '../utils/apiCall';
import { store } from '../redux/store';

const safeCall = async (...args) => { try { return await callApi(...args); } catch { return { status: 0, message: 'Could not confirm the change. Check the current status before retrying.' }; } };
const detail = response => response?.data?.detail || response?.error?.detail || response?.message || 'Could not confirm the change. Refresh the current appointment details.';
const waiting = state => ['waiting_for_cancellation', 'archive_pending'].includes(state);

// Own only the selected appointment: mutation responses never dispatch unscoped
// lead reads or legacy completion flags. Polls are reads, never mutation retries.
export default function useAppointmentOutcome(selection, active, onComplete) {
  const [result, setResult] = useState(null);
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState('');
  const [uncertain, setUncertain] = useState(false);
  const lock = useRef(false);
  const generation = useRef(0);
  const timer = useRef(null);
  const expectedRef = useRef(null);
  const archiveRef = useRef(false);
  const actionRef = useRef(null);
  const uncertainRef = useRef(false);
  const markUncertain = value => { uncertainRef.current = value; setUncertain(value); };
  const pathname = useRef(window.location.pathname);
  const base = selection ? `/api/v1/practices/${selection.practiceId}/leads/${selection.leadId}/appointments/${selection.appointmentId}/` : '';
  useEffect(() => {
    generation.current += 1;
    setResult(selection?.appointment?.calcom?.cancel_archive ? { cancel_archive: selection.appointment.calcom.cancel_archive, calcom: selection.appointment.calcom } : null);
    setMessage(selection?.appointment?.calcom?.cancel_archive?.detail || (['pending', 'unknown', 'failed'].includes(selection?.appointment?.calcom?.sync_status) ? 'The previous booking change needs confirmation. Check the current status.' : '')); markUncertain(false); setBusy(false); lock.current = false; expectedRef.current = null; actionRef.current = null; archiveRef.current = Boolean(selection?.appointment?.calcom?.cancel_archive); pathname.current = window.location.pathname;
    return () => { generation.current += 1; clearTimeout(timer.current); };
  }, [base, active]); // eslint-disable-line react-hooks/exhaustive-deps
  const current = captured => active && generation.current === captured && pathname.current === window.location.pathname
    && String(store.getState().practiceTreatmentList?.practiceId) === String(selection?.practiceId)
    && String(store.getState().lead?.followLead?.id) === String(selection?.leadId);
  const apply = (response, captured, archive, expected) => {
    if (!current(captured)) return false;
    if (response.status < 200 || response.status >= 300) {
      if (response.data?.calcom || response.data?.cancel_archive) setResult(response.data);
      setMessage(detail(response)); return false;
    }
    const { data } = response;
    setResult(data);
    if (response.status === 200 && (data.calcom?.sync_status === 'synced' || (archive && data.cancel_archive && data.cancel_archive.state !== 'unknown'))) markUncertain(false);
    if (archive ? data.cancel_archive?.state === 'archived' : response.status !== 202 && data.calcom?.sync_status !== 'pending' && data.calcom?.sync_status !== 'unknown' && data.calcom?.sync_status !== 'failed' && expected?.(data)) {
      onComplete(); return false;
    }
    setMessage(data.cancel_archive?.detail || data.detail || data.calcom?.last_error || 'Change pending. The appointment updates after confirmation.');
    return response.status === 202 || (archive ? waiting(data.cancel_archive?.state) : data.calcom?.sync_status === 'pending');
  };
  const poll = (captured, archive, expected, attempt = 0, reconciling = false) => {
    if (!current(captured)) return;
    timer.current = setTimeout(async () => {
      if (!current(captured)) return;
      const response = await safeCall(`${base}${archive ? 'cancel-and-archive/' : ''}`);
      const again = apply(response, captured, archive, expected);
      const stillChecking = reconciling && (archive ? response.data?.cancel_archive?.state === 'unknown' : response.data?.calcom?.sync_status === 'unknown');
      if ((again || stillChecking) && attempt < 11) poll(captured, archive, expected, attempt + 1, reconciling);
      else if (current(captured)) { lock.current = false; setBusy(false); }
    }, 5000);
  };
  const run = async (action, payload) => {
    const captured = generation.current;
    if (lock.current || uncertainRef.current || !selection || !current(captured)) return;
    lock.current = true; setBusy(true); setMessage('');
    const archive = action === 'archive';
    archiveRef.current = archive;
    actionRef.current = action;

    const paths = { archive: 'cancel-and-archive/', cancel: 'cancelled/', unable: 'failed_to_attend/', reschedule: '', rebook: '' };
    const response = await safeCall(action === 'rebook' ? base.replace(/[^/]+\/$/, '') : `${base}${paths[action]}`, action === 'reschedule' ? 'patch' : 'post', payload);
    let expected = null;
    if (action === 'cancel') expected = data => data.status === 'CANCELLED';
    if (action === 'unable') expected = data => ['FAILED_TO_ATTEND', 'FAILED_TO_ATTEND_CONSULTATION'].includes(data.status);
    if (action === 'rebook') expected = data => Boolean(data.id && String(data.id) !== String(selection.appointmentId) && ['BOOKED', 'CONSULTATION_BOOKED'].includes(data.status));
    if (action === 'reschedule') expected = data => moment(data.datetime, ['DD-MM-YYYY HH:mm:ss.SSSSSSZ', 'DD-MM-YYYY HH:mm:ss', moment.ISO_8601], true).isSame(moment(payload.datetime, ['DD-MM-YYYY HH:mm:ss.SSSSSSZ', moment.ISO_8601], true));
    if (current(captured) && (!response.status || response.status >= 500)) markUncertain(true);
    if (!current(captured)) return;
    expectedRef.current = expected;
    if (apply(response, captured, archive, expected)) poll(captured, archive, expected);
    else if (current(captured)) { lock.current = false; setBusy(false); }
  };
  const refresh = async () => {
    const captured = generation.current;
    if (lock.current || !selection || !current(captured)) return;
    lock.current = true; setBusy(true);
    const archive = archiveRef.current || Boolean(result?.cancel_archive);
    if (archive) {
      const response = await safeCall(`${base}cancel-and-archive/`);
      const again = apply(response, captured, true, null);
      if (!current(captured)) return;
      if (response.data?.cancel_archive?.state !== 'unknown') {
        if (again) poll(captured, true, null);
        else { lock.current = false; setBusy(false); }
        return;
      }
    }
    if (!selection.calcomDerived) {
      const response = await safeCall(base);
      apply(response, captured, false, expectedRef.current);
      if (current(captured) && response.status === 200) {
        if (actionRef.current === 'rebook') setMessage('Could not confirm whether the new booking was created. Check the lead’s appointments before booking again.');
        else markUncertain(false);
      }
      if (current(captured)) { lock.current = false; setBusy(false); }
      return;
    }
    const response = await safeCall(`${base}calcom-refresh/`, 'post', {});
    if (!current(captured)) return;
    if (response.status >= 200 && response.status < 300) poll(captured, archive, expectedRef.current, 0, true);
    else { setMessage(detail(response)); lock.current = false; setBusy(false); }
  };
  return { run, refresh, result, busy, uncertain, message, retryArchive: () => run('archive', { retry: true }), canRetryArchive: Boolean(result?.cancel_archive?.can_retry_archive), retryCancel: () => run('archive', { retry: true }), canRetryCancel: Boolean(result?.cancel_archive?.can_retry_cancel) };
}
